import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';

/**
 * Tracks browser fullscreen state and exposes two ways to enter it:
 *
 *  - {@link toggle} — fullscreen the whole app while keeping the navbar visible
 *    (the profile-menu "Fullscreen" action).
 *  - {@link toggleImmersive} — fullscreen and hide the navbar for an edge-to-edge
 *    view (the POS "Enter fullscreen" button).
 *
 * The distinction is the {@link immersive} flag: both enter the same browser
 * fullscreen, but only immersive mode hides app chrome (see {@link chromeHidden}).
 *
 * Centralised so the trigger buttons and the app shell all react to the same
 * state. Stays accurate even when the user exits via Esc or F11, since it
 * listens to the document's `fullscreenchange` event rather than tracking state
 * manually — and leaving fullscreen always restores the chrome.
 */
@Injectable({ providedIn: 'root' })
export class FullscreenService {
  private readonly doc = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly isFullscreen = signal(this.isBrowser && !!this.doc.fullscreenElement);
  private readonly immersive = signal(false);

  /**
   * Whether app chrome (the navbar) should be hidden. Only true while genuinely
   * fullscreen AND in immersive mode, so a failed/late fullscreen request can
   * never strand the UI with a hidden navbar.
   */
  readonly chromeHidden = computed(() => this.isFullscreen() && this.immersive());

  constructor() {
    if (!this.isBrowser) return;
    this.doc.addEventListener('fullscreenchange', () => {
      const active = !!this.doc.fullscreenElement;
      this.isFullscreen.set(active);
      if (!active) this.immersive.set(false); // leaving fullscreen restores chrome
    });
  }

  /** Toggle fullscreen for the whole app, keeping the navbar visible. */
  toggle(): void {
    this.immersive.set(false);
    this.request();
  }

  /** Toggle fullscreen in immersive mode — also hides the navbar (POS). */
  toggleImmersive(): void {
    if (!this.isBrowser) return;
    // Only arm immersive when entering; exiting lets the listener clear it.
    this.immersive.set(!this.doc.fullscreenElement);
    this.request();
  }

  private request(): void {
    if (!this.isBrowser) return;
    const request = this.doc.fullscreenElement
      ? this.doc.exitFullscreen()
      : this.doc.documentElement.requestFullscreen();
    // Swallow rejections (e.g. the browser blocks the request); the
    // fullscreenchange listener keeps the signals accurate regardless.
    request?.catch(() => {});
  }
}
